import { Observable } from "rxjs";

export class ModelObserver {
    paused = false;
    timer: any = null;
    triggered = false;
    running = false;
    model: object = null;
    changes: object = {};

    private triggering = false;
    // eslint-disable-next-line sonarjs/public-static-readonly
    static observerIdPropertyName = "_observerId";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static entityIdPropertyName = "_entityId";

    constructor(model: object, private readonly callback: (model: object, changes: object) => Observable<any>, entityIdPropertyName: string, properties: string[] = null) {

        if (properties !== null) {
            this.addTriggerToProperties(model, properties, 0);
        } else {
            this.addTriggerToAllProperties(model, 0);
        }

        this.model = model;
        this.model[ModelObserver.observerIdPropertyName] = this.guid();
        this.model[ModelObserver.entityIdPropertyName] = entityIdPropertyName;
    }

    pause() {
        this.paused = true;
    }

    continue() {
        this.paused = false;
        if (this.triggering) {
            this.invoke();
        }
    }

    addTriggerToProperties(model: object, properties: string[], timeout?: number) {
        const items = [];
        if (model instanceof Array) {
            items.push(...model);
        } else {
            items.push(model);
        }

        items.forEach(item => {
            properties.forEach(propertyName => {
                if (this.canAddProperty(item, propertyName)) {
                    this.addTrigger(item, propertyName, timeout);
                }
            });
        });
    }

    addTriggerToAllProperties(model: object, timeout?: number, ignoreMembers?: string[]) {
        const items = [];
        if (model instanceof Array) {
            items.push(...model);
        } else {
            items.push(model);
        }

        items.forEach(item => {
            for (const propertyName in item) {
                if (this.canAddProperty(item, propertyName, ignoreMembers)) {
                    this.addTrigger(item, propertyName, timeout);
                }
            }
        });
    }

    addTrigger(model: object, propertyName: string, timeout?: number) {

        if (model[propertyName] instanceof Array) {
            this.bindArray(model[propertyName], propertyName, timeout);
        }

        let container = model[propertyName];
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const observer = this;
        if (model[propertyName] === undefined) {
            model[propertyName] = null;
        }
        Object.defineProperty(
            model,
            propertyName,
            {
                set(v) {
                    if (container === v) {
                        return;
                    }
                    container = v;
                    observer.changes[propertyName] = v;
                    observer.updateTimer(timeout);
                    if (v instanceof Array) {
                        observer.bindArray(container, propertyName, timeout);
                    }
                },
                get() {
                    return container;
                }
            });
    }

    updateTimer(timeout = 0) {
        this.triggered = true;
        if (this.timer != null) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(this.invoke.bind(this), timeout);
    }

    invoke() {
        this.triggering = true;
        if (this.paused) {
            return;
        }
        this.running = true;
        this.callback(this.model, this.changes).subscribe(() => {
            this.running = false;
        });

        this.triggered = false;
        this.timer = null;
        this.triggering = false;
    }

    private canAddProperty(item: any, propertyName: string, ignoreMembers?: string[]): boolean {
        return (Object.hasOwn(item, propertyName) && (ignoreMembers == null || ignoreMembers.indexOf(propertyName) === -1));
    }

    private bindArray(array: any, propertyName: string, timeout?: number) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const observer = this;
        array.push = (argArray) => {
            Array.prototype.push.apply(array, argArray);
            observer.changes[propertyName] = array;
            observer.updateTimer(timeout);
        };
    }

    private guid() { // replace with "real" guid generator
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            // eslint-disable-next-line no-bitwise, sonarjs/pseudo-random
            const r = Math.random() * 16 | 0;
            // eslint-disable-next-line no-bitwise
            const v = c === "x" ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}
